/** ***********
*
* <manage-user-role.component.ts v1.0>
*
* Copyright (c) 2023 AIRBUS OPERATIONS
*
* Contributors:
*  GARY Adrien (NG635E8) [adrien.gary.external@airbus.com]
*
* All rights reserved.
**************/

import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl, ValidatorFn } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, forkJoin, Observable, Subscription } from 'rxjs';
import { delay, filter, map, startWith, take } from 'rxjs/operators';
import { FamilyRight, ParentFamilyRight } from 'src/app/models/family';
import { ProfileParentFamily } from 'src/app/models/profile';
import { User } from 'src/app/models/user';
import { RoleLevel, RoleLevelEnum, SpecificRoleDialogData, SpecificRoleDialogResult } from 'src/app/models/user-right';
import { FamilyService } from 'src/app/services/family.service';
import { UserRightService } from 'src/app/services/user-right.service';
import { UserRightEffects } from 'src/app/store/effects/user-right.effects';
import * as userRightActions from 'src/app/store/actions/user-right.action';
import { selectProfileParentFamilies$, selectProfiles$ } from '../../../store/selectors/profile.selectors';
import { selectAllUsers$, selectUserRightLoading$, selectUserRightTree$ } from 'src/app/store/selectors/user-right.selectors';
import { selectAllUsersParentFamilyRightsFilters$ } from '../../../store/selectors/filter.selectors';
import { DialogSpecificRoleComponent } from '../dialog/dialog-specific-role/dialog-specific-role.component';
import { selectSiteState$ } from 'src/app/store/selectors/site.selectors';
import { selectCompaniesState$ } from 'src/app/store/selectors/company.selectors';
import { cloneDeep } from 'lodash';
import { MatChipInput } from '@angular/material/chips';
import { Actions as UserGroupActions } from '../_shared/user-groups/user-groups.component';
import { FilterComponent } from '../../filter/filter.component';
import { detailExpand, rotate } from '../../shared/animations';
import { getLevelSortingMagnitude } from '../_shared/sort-helper';

@Component({
    selector: 'app-manage-user-role',
    templateUrl: './manage-user-role.component.html',
    styleUrls: ['./manage-user-role.component.scss'],
    animations: [detailExpand, rotate(180)],
    standalone: false
})
export class ManageUserRoleComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild(MatSort, { static: false }) sort: MatSort;
    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
    @ViewChild(MatChipInput) groupInput: MatChipInput;

    /**
     * For filter component
     */
    @ViewChild(FilterComponent) _filterComponent;
    public parentFamilies$: Observable<ParentFamilyRight[]>;
    public view = 'manageUserRoleView';

    public loading: boolean;
    public isAdmin: boolean;
    public isGroupAdmin: boolean;
    public allUsers: User[] = [];
    public filteredUsers$: Observable<User[]>;
    public selectedUserId = '';
    public selectedUser: User;
    public userIdFormControl = new UntypedFormControl();
    public manageableParentFamilies: ProfileParentFamily[];
    public isLoadingUsers$: Observable<boolean>;
    public isLoadingTable: boolean;
    public dataSource = new MatTableDataSource();
    public displayedColumns: string[] = ['name', 'company', 'site', 'description', 'level', 'actionColumns'];
    public columnNames = {
        name: this.translate.instant('NAME'),
        company: this.translate.instant('COMPANY'),
        site: this.translate.instant('SITE'),
        description: this.translate.instant('DESCRIPTION'),
        level: this.translate.instant('RIGHT_LEVEL'),
        actionColumns: ''
    };
    public readonly qtyGroupsToDisplay = 5;
    public readonly GROUP_ACTIONS = UserGroupActions;

    private _subscription: Subscription = new Subscription();

    /**
     * store pending level value in order to avoid ugly visual effect with the radiobutton
     */
    public pendingLevels: { [familyOrParentId: string]: RoleLevel } = {};
    public disabledRadioButton: { [familyOrParentId: string]: boolean } = {};

    public true = false;

    constructor(
        public translate: TranslateService,
        private _cd: ChangeDetectorRef,
        private _userRightService: UserRightService,
        private _dialog: MatDialog,
        private store: Store,
        private _familyService: FamilyService,
        private _userRightEffects: UserRightEffects
    ) { }

    ngOnInit() {
        this._userRightService.checkUserLoaded();
        // this.pendingLevels = this._userRightService.pendingLevels;

        // Prepare data source for filters (subscribed from the template)
        this.parentFamilies$ = this.store.select(selectUserRightTree$).pipe(
            map(parentFamilies => parentFamilies.filter(
                parentFamily => parentFamily.userId === this.selectedUserId
            )),
        );

        // retrieve all user in order to suggest users from the user ID field
        const allUsers$ = this.store.select(selectAllUsers$);
        this.isLoadingUsers$ = this.store.select(selectUserRightLoading$);
        this._subscription.add(
            allUsers$.subscribe(allUsers => {
                this.allUsers = allUsers;
            })
        );

        // Checking for users who have no roles, to reset filter conditions
        this._subscription.add(
            this.parentFamilies$.subscribe(allUsersParentFamily => {
                const validateUserRole = allUsersParentFamily.filter(
                    parent => parent.level !== RoleLevelEnum.basic || parent.families.some(
                        family => family.level !== RoleLevelEnum.basic
                    )
                );
                if (validateUserRole.length === 0) {
                this._filterComponent?.resetFilters();
                }
            })
        );

        // init the user form control
        this.userIdFormControl = new UntypedFormControl('', this.userFormControlValidator());
        // Subscribe to user ID field value change in order to update the autocomplete suggestion
        this.filteredUsers$ = combineLatest([
          allUsers$,
          this.userIdFormControl.valueChanges.pipe(startWith('')),
        ]).pipe(
          map(([, _value]) => this._filterUsers(_value))
        );

        /* subscribe to value change of user ID selected
         If the user exists, initialize the table data */
        this._subscription.add(
            this.userIdFormControl.valueChanges.pipe(
                filter(() => this.userIdFormControl.valid)
            )
            .subscribe(
                () => {
                    const user = this.userIdFormControl.value;
                    this._onChangeSelectedUser(user);
                }
            )
        );

        this._subscription.add(
            // Listen for group change to refresh the data
            this._userRightEffects.effectSubject.pipe(
                filter(({ action }) => [
                    userRightActions.ActionTypes.ADD_USERS_TO_GROUP,
                    userRightActions.ActionTypes.REMOVE_USERS_FROM_GROUP,
                ].includes(action as userRightActions.ActionTypes)),
                // Wait 0.5 second due to role assignment in stream
                delay(500),
            ).subscribe(
                () => {
                    this._fetchData(this.selectedUserId);
                }
            )
        );

        /* subscribe to manageable parent families right in order to handle the visibility of the action button
        (available only if the user is manager or administrator) */
        this._subscription.add(this.store.select(selectProfileParentFamilies$).subscribe((parentFamilies) => {
            this.manageableParentFamilies = [];
            for (const parent of parentFamilies) {
                if (parent.level === RoleLevelEnum.administrator || parent.level === RoleLevelEnum.manager) {
                    this.manageableParentFamilies.push(parent);
                }
            }
        }));

        this._subscription.add(
            this.store.select(selectProfiles$).subscribe(data => {
                this.isAdmin = data.profiles.userRights.isAdmin;
                this.isGroupAdmin = data.profiles.userRights.isGroupAdmin;
            })
        );
    }

    ngAfterViewInit() {
        this._subscription.add(this.dataSource.connect().subscribe((data) => {
            if (data.length) {
                this._cd.markForCheck();
            }
        }));
    }

    /** Initialize table data based on the user ID
     * @param userId The user ID
     */
    private _initTable(userId: string) {
        this.dataSource.data = [];
        this.isLoadingTable = true;
        this._cd.markForCheck();

        this._subscription.add(combineLatest([
            this.store.select(selectAllUsersParentFamilyRightsFilters$),
            this.store.select(selectSiteState$),
            this.store.select(selectCompaniesState$),
        ]).pipe(
            filter(([, sites, companies]) => sites.loaded && companies.loaded)
        ).subscribe(([alluserParentFamiliesRightFiltered, sites, companies]) => {
            const parentFamiliesRightDeepCopy = cloneDeep(alluserParentFamiliesRightFiltered.filter(parent => parent.userId.toLowerCase() === userId.toLowerCase()));
            parentFamiliesRightDeepCopy.forEach(parentFamilyRight => {
                parentFamilyRight.level = this.pendingLevels[parentFamilyRight.id] || (parentFamilyRight.level === RoleLevelEnum.administrator ? RoleLevelEnum.manager : parentFamilyRight.level);
                parentFamilyRight.site = sites.data.find(site => site.id === parentFamilyRight.siteId);
                parentFamilyRight.company = companies.data.find(company => company.id === parentFamilyRight.site.companyId);
                parentFamilyRight.families.forEach(familyRight => {
                    familyRight.level = (this.pendingLevels[familyRight.id]) || (familyRight.level === RoleLevelEnum.administrator ? RoleLevelEnum.manager : familyRight.level);
                    familyRight = { ...familyRight, site: parentFamilyRight.site, company: parentFamilyRight.company };
                });
                parentFamilyRight.familyFromGroup = parentFamilyRight.families.some(
                  ({ fromGroupId }) => fromGroupId
                );
            });
            this.dataSource.data = parentFamiliesRightDeepCopy;
            this.dataSource.paginator = this.paginator;
        }));

        this._fetchData(userId);

        // sorting
        this.dataSource.sortingDataAccessor = (item: ParentFamilyRight, property) => {
            switch (property) {
                case 'name': return item.name;
                case 'description': return item.description;
                case 'company': return item.company.name;
                case 'site': return item.site.name;
                case 'level': return getLevelSortingMagnitude(item.level);
                default: return item[property];
            }
        };
        this.dataSource.sort = this.sort;
    }

    /**
     * Fetch the data for the table
     * @param userId The user id
     */
    private _fetchData(userId: string) {
        this.dataSource.data = [];
        this.isLoadingTable = true;
        this._cd.detectChanges();
        /* Fecth data */
        this._subscription.add(
            forkJoin([this._userRightService.getUserParentFamilyRights(userId), this._familyService.getUserFamiliesRights(userId)])
                .subscribe(
                    ([userParentFamiliesRight, userFamiliesRight]) => {
                        this.store.dispatch(new userRightActions.SuccessUserRightTree(userParentFamiliesRight, userFamiliesRight));
                        this.isLoadingTable = false;
                    }
                )
        );
    }

    /**
     * Fired after confirmation of specific role update
     * @param user the user to modify
     */
    public openDialogSpecificRole(user: SpecificRoleDialogData) {
        this.loading = true;
        const dialogData = user;
        const dialog = this._dialog.open(DialogSpecificRoleComponent, {
            data: dialogData,
            minWidth: '18vw'
        });
        this._subscription.add(
            dialog.afterClosed().subscribe((dialogResult: SpecificRoleDialogResult) => {
                switch (dialogResult?.action) {
                    case 'cancel':
                        // do nothing
                        this.loading = false;
                        break;
                    case 'update':
                        this.store.dispatch(new userRightActions.UpdateSpecificRole({ option: dialogResult.option, userId: user.id }));
                        user.option = dialogResult.option;
                        this._onChangeSelectedUser(user);
                        // subscribe to effect observer in order to manage loader and dialog closure
                        this._subscription.add(this._userRightEffects.effectSubject.pipe(take(1)).subscribe((effectResult) => {
                            if (effectResult.result === 'success') {
                                this.loading = false;
                            }
                        }));
                        break;
                    default:
                        this.loading = false;
                        break;
                }
            })
        );
    }

    /** Fired on click on a parent family row
     */
    public onClickParentFamilyRow(element: ParentFamilyRight) {
        element.expanded = !element.expanded;
    }

    /**
     * Fired after clicking the radio button to update role
     * @param parentFamily The parentFamily to modify
     * @param family Optional - The family to modify
     * @param role The Role to modify
     */
    onClickModifyRole(parentFamily: ParentFamilyRight, level?: RoleLevel, family?: FamilyRight) {
        const oldParentFamilyData = { ...parentFamily };
        const oldFamilyData = family ? { ...family } : undefined;


        if (family) {
            // check whether the button is disabled
            if (this.disabledRadioButton[family.id]) {
                return;
            }
            // handle disabling and loading
            this.pendingLevels[family.id] = level;
            this.disabledRadioButton[family.id] = true;
            family.level = level;
        } else {
            // check whether the button is disabled
            if (this.disabledRadioButton[parentFamily.id]) {
                return;
            }
            this.pendingLevels[parentFamily.id] = level;
            parentFamily.level = level;
        }

        if (parentFamily && !family) {
            // disabled all families
            parentFamily.families.forEach((_families) => this.disabledRadioButton[_families.id] = true);
        }

        this.disabledRadioButton[parentFamily.id] = true;

        /**
         * Handle the disabled state and the selected value of the radio buttons
         */
        const handleRadioButtonState = () => {
            setTimeout(() => {
                if (family) {
                    delete this.pendingLevels[family.id];
                    delete this.disabledRadioButton[family.id];
                } else {
                    delete this.pendingLevels[parentFamily.id];
                }
                delete this.disabledRadioButton[parentFamily.id];

                if (parentFamily && !family) {
                    // disabled all families
                    parentFamily.families.forEach((_families) => this.disabledRadioButton[_families.id] = false);
                }

            }, 0);
        };

        /**
         * Set back the level value after an error
         */
        const handleRadioButtonStateError = () => {
            setTimeout(() => {
                handleRadioButtonState();
                if (family) {
                    family.level = oldFamilyData.level;
                } else {
                    parentFamily.level = oldParentFamilyData.level;
                }
            }, 0);
        };

        let observable: Observable<any>;
        if (level === RoleLevelEnum.basic) {
            observable = this._userRightService.removeUserRole(this.selectedUserId, parentFamily, family);
        } else {
            observable = this._userRightService.handleUserRoleChange(this.selectedUserId, oldParentFamilyData, level, oldFamilyData);
        }

        this._subscription.add(observable.subscribe({
            next: handleRadioButtonState,
            error: handleRadioButtonStateError,
        }));
    }

    /**
     * Get the translated name of table column header
     * @param value - The name of the column to be translated
     */
    public getRealName(value: string): string {
        return this.columnNames[value];
    }

    /**
     * Called when the user change the paginator page
     * - Aims to auto scroll to the top of the table
     */
    public scrollUp() {
        const table = document.querySelector('.mat-table');
        table.scrollIntoView({ behavior: 'smooth' });
        window.scrollTo(0, 0);
    }

    /**
     * Used in order to display the NG ID in the NG ID input
     */
    public getNgID(user: User) {
        return user?.userId || '';
    }

    /**
     * Form control validator
     * - check whether the user exists
     */
    public userFormControlValidator(): ValidatorFn {
        return () => {
            return !this._isUserIdValid() ? { user: { value: this.userIdFormControl.value } } : null;
        };
    }

    /** Called when the User ID filled is valid
    * @param selectedUserid The user ID
    */
    private _onChangeSelectedUser(selectedUser: User) {
        this.selectedUserId = selectedUser.id;
        this.selectedUser = selectedUser;
        this._initTable(this.selectedUserId);
    }

    /** Check wether the user exists
     * - Check if the user ID is among the the all user list
     */
    private _isUserIdValid(): boolean {
        return this.allUsers.find((user) => {
            if (this.userIdFormControl.value) {
                return user === this.userIdFormControl.value;
            } else {
                return false;
            }
        })
            ? true : false;
    }

    /**
     * Filter the user list base on the value entered in the user ID field
     * @param value The value filled in the user ID field
     */
    private _filterUsers(value: string | User): User[] {
        if (typeof value === 'string') {
            const filterValue = value.toLowerCase();
            return this.allUsers.filter(
                (option) => {
                    return (
                        // option.lastname.toLowerCase().includes(filterValue)
                        // || option.firstname.toLowerCase().includes(filterValue)
                        // || option.userId.toLowerCase().includes(filterValue)
                        option.email?.toLocaleLowerCase().includes(filterValue) ||
                        option.userId?.toLowerCase().includes(filterValue)
                    );
                });
        } else { return []; }
    }

    /**
     * Called to display the action icon if the current user is manager for the given parent family
     */
    public isParentManager(parentFamily: ParentFamilyRight) {
        return this.manageableParentFamilies.find(item => item.id === parentFamily.id) ? true : false;
    }

    ngOnDestroy() {
        this._subscription.unsubscribe();
    }
}
